import { useCallback } from 'react';

import { updateTrackingContext } from '@features/Energy/useEnergyDetails/updateTrackingContext';
import { BundleBasketFragment } from '@generated/gql/graphql';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  useTrackSignupEnergyAddedToBasket,
  useTrackSignupEnergyFaqExpanded,
  useTrackSignupEnergyFaqsHeaderDisplayedOnce,
  useTrackSignupEnergySmartMeterPromoDisplayedOnce,
  useTrackSignupEvBannerDisplayedOnce,
  useTrackSignupEvLinkClickedOnce,
} from '@tracking/trackingEvents';
import { buildOptimizelyEntity } from '@tracking/utils/buildOptimizelyEntity';

import { parseEnergyTrackingContext } from '../energyTrackingContext';

const useEnergyTracking = () => {
  const { trackingContexts } = useTrackingContext();
  const energyAddedToBasket = useTrackSignupEnergyAddedToBasket();
  const evBannerDisplayedOnce = useTrackSignupEvBannerDisplayedOnce();
  const evLinkClickedOnce = useTrackSignupEvLinkClickedOnce();
  const smartMeterBannerDisplayedOnce =
    useTrackSignupEnergySmartMeterPromoDisplayedOnce();
  const trackFaqExpanded = useTrackSignupEnergyFaqExpanded();
  const faqHeaderDisplayedOnce = useTrackSignupEnergyFaqsHeaderDisplayedOnce();

  const trackEnergyAddedToBasket = (basket: Nullable<BundleBasketFragment>) => {
    if (!basket) {
      // Should never happen
      throw new Error('submitEnergy: missing basket response');
    }

    if (!basket.energy) return;

    const updatedTrackingContext = updateTrackingContext(
      trackingContexts,
      basket as BundleBasketFragment
    );

    energyAddedToBasket(parseEnergyTrackingContext(basket.energy), {
      contexts: [...updatedTrackingContext, ...buildOptimizelyEntity()],
    });
  };

  const trackEvLinkClicked = () =>
    evLinkClickedOnce({}, { contexts: trackingContexts });

  const trackEvBannerDisplayed = useCallback(
    () => evBannerDisplayedOnce({}, { contexts: trackingContexts }),
    [evBannerDisplayedOnce, trackingContexts]
  );

  const trackSmartMeterBannerDisplayed = useCallback(
    () => smartMeterBannerDisplayedOnce({}, { contexts: trackingContexts }),
    [smartMeterBannerDisplayedOnce, trackingContexts]
  );

  const trackFaqLinkClicked = useCallback(
    (question: string) => {
      trackFaqExpanded({ question }, { contexts: trackingContexts });
    },
    [trackFaqExpanded, trackingContexts]
  );

  const trackFaqHeaderDisplayed = useCallback(() => {
    faqHeaderDisplayedOnce({}, { contexts: trackingContexts });
  }, [faqHeaderDisplayedOnce, trackingContexts]);

  return {
    trackEnergyAddedToBasket,
    trackEvLinkClicked,
    trackEvBannerDisplayed,
    trackSmartMeterBannerDisplayed,
    trackFaqLinkClicked,
    trackFaqHeaderDisplayed,
  };
};

export { useEnergyTracking };
